import { TagChips } from "@/components/teacher/Social";
import { ContentBadges, trackView } from "@/components/teacher/ContentBadges";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Pencil, Plus, Upload } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, downloadFile, errMsg } from "@/lib/api";
import { dataTexts, trNormalize } from "@/lib/chem";
import { QuestionRow } from "@/components/teacher/QuestionRow";
import { QuestionFilters } from "@/components/teacher/QuestionFilters";
import { QuestionEditor } from "@/components/teacher/QuestionEditor";
import { ImportDialog } from "@/components/teacher/ImportDialog";
import { TransferDialog } from "@/components/teacher/TransferDialog";
import { PoolNameDialog } from "@/components/teacher/PoolNameDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const EMPTY = { search: "", points: "", topic: "", type: "" };

function matches(q, f) {
  if (f.points && String(q.points) !== f.points) return false;
  if (f.topic && q.topic !== f.topic) return false;
  if (f.type && q.type !== f.type) return false;
  if (!f.search) return true;
  return trNormalize([q.text, q.topic, q.explanation, ...q.options.map((o) => o.text), ...dataTexts(q)].join(" ")).includes(trNormalize(f.search));
}

const ExportMenu = ({ poolId }) => {
  const run = async (format) => {
    try {
      const { data } = await api.get(`/pools/${poolId}/export`, { params: { format } });
      downloadFile(data.content, data.filename, data.mime);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-11 border-[rgba(71,85,105,0.45)]" data-testid="export-menu-button"><Download className="h-4 w-4 mr-2" />Dışa aktar</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => run("json")} data-testid="export-json-action">JSON (tüm alanlar)</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run("csv")} data-testid="export-csv-action">CSV</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function PoolDetailPage() {
  const { poolId } = useParams();
  const [pool, setPool] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [filters, setFilters] = useState(EMPTY);
  const [editor, setEditor] = useState({ open: false, question: null });
  const [importOpen, setImportOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [transfer, setTransfer] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const load = useCallback(async () => {
    try {
      const [p, q] = await Promise.all([api.get(`/pools/${poolId}`), api.get(`/pools/${poolId}/questions`)]);
      setPool(p.data);
      setQuestions(q.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [poolId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { trackView("pools", poolId); }, [poolId]);

  const topics = useMemo(() => [...new Set(questions.map((q) => q.topic).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [questions]);
  const points = useMemo(() => [...new Set(questions.map((q) => q.points))].sort((a, b) => a - b), [questions]);
  const filtered = useMemo(() => questions.filter((q) => matches(q, filters)), [questions, filters]);
  const filtering = Object.values(filters).some(Boolean);

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(questions, questions.findIndex((q) => q.id === active.id), questions.findIndex((q) => q.id === over.id));
    setQuestions(next);
    try { await api.put(`/pools/${poolId}/questions/order`, { ids: next.map((q) => q.id) }); } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const actions = {
    edit: (q) => setEditor({ open: true, question: q }),
    duplicate: async (q) => { try { await api.post(`/questions/${q.id}/duplicate`); toast.success("Soru çoğaltıldı"); load(); } catch (e) { toast.error(errMsg(e)); } },
    transfer: (q, mode) => setTransfer({ question: q, mode }),
    remove: (q) => setToDelete(q),
  };

  const deleteQuestion = async () => {
    try { await api.delete(`/questions/${toDelete.id}`); toast.success("Soru silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  const rename = async (name) => {
    try { await api.patch(`/pools/${poolId}`, { name }); toast.success("Havuz adı güncellendi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!pool) return <p className="eyebrow" data-testid="pool-loading">Yükleniyor…</p>;

  return (
    <div className="space-y-7">
      <Link to="/ogretmen/havuzlar" className="inline-flex items-center gap-2 text-slate-400 hover:text-white" data-testid="back-to-pools-link">
        <ArrowLeft className="h-4 w-4" /> Havuzlar
      </Link>
      <div className="flex flex-col xl:flex-row xl:items-end gap-4">
        <div className="flex-1 min-w-0">
          <p className="eyebrow">{pool.question_count} soru {pool.archived && "· arşivde"}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1 flex items-center gap-3 break-words" data-testid="pool-title">
            {pool.name}
            <button onClick={() => setRenameOpen(true)} className="p-2 rounded-md hover:bg-[rgba(71,85,105,0.45)]" data-testid="pool-rename-button" aria-label="Yeniden adlandır"><Pencil className="h-5 w-5" /></button>
          </h1>
          <div className="mt-2 flex flex-wrap gap-2"><TagChips item={pool} /><ContentBadges kind="pools" item={pool} onChange={load} /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="h-11 border-[rgba(71,85,105,0.45)]" onClick={() => setImportOpen(true)} data-testid="import-button"><Upload className="h-4 w-4 mr-2" />İçe aktar</Button>
          <ExportMenu poolId={poolId} />
          <Button className="h-11 px-5 font-black" onClick={() => setEditor({ open: true, question: null })} data-testid="add-question-button"><Plus className="h-5 w-5 mr-1" />Soru ekle</Button>
        </div>
      </div>
      <QuestionFilters filters={filters} setFilters={setFilters} points={points} topics={topics} />
      {filtering && <p className="text-sm text-slate-400" data-testid="filter-info">{filtered.length} / {questions.length} soru gösteriliyor. Sürükle-bırak sıralama filtre kapalıyken çalışır.</p>}
      {filtered.length === 0 ? (
        <div className="panel p-10 text-slate-400" data-testid="questions-empty">{questions.length ? "Filtreye uyan soru yok." : "Bu havuzda henüz soru yok. \"Soru ekle\" veya \"İçe aktar\" ile başlayın."}</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={filtered.map((q) => q.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3" data-testid="question-list">
              {filtered.map((q, i) => <QuestionRow key={q.id} q={q} index={i} dragDisabled={filtering} actions={actions} />)}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <QuestionEditor open={editor.open} onOpenChange={(open) => setEditor((s) => ({ ...s, open }))} poolId={poolId}
        question={editor.question} topics={topics} onSaved={load} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} poolId={poolId} onDone={load} />
      <TransferDialog state={transfer} onClose={() => setTransfer(null)} currentPoolId={poolId} onDone={load} />
      <PoolNameDialog open={renameOpen} onOpenChange={setRenameOpen} title="Havuzu yeniden adlandır" initial={pool.name} onSubmit={rename} />
      <ConfirmDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)} title="Soru silinsin mi?"
        description="Soru havuzdan kalıcı olarak silinecek. Geçmiş oyun kayıtları (soru kopyaları) etkilenmez." onConfirm={deleteQuestion} />
    </div>
  );
}
